import { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Footer from '../../components/Footer';
import Header from '../../components/Header/Header';
import { HomeWrapper } from '../GamePage/GamePage.styled';
import { PlayerContext } from '../../contexts/playerContext';
import { StyleProfitContainer, StyleTopBar, StyleTotalView, StyleLeaderboardTable, StylePagination, StyledTableNameFilter } from './ProfitPage.styled';
import Select, { SingleValue } from 'react-select';

interface Option {
  value: string;
  label: string;
}

const API_ENDPOINT = process.env.REACT_APP_API_ENDPOINT;

export const ProfitPage: React.FC = () => {
  const playerContext = useContext(PlayerContext);
  const navigate = useNavigate();

  const today = new Date();

  const [startDate, setStartDate] = useState<string>(`${today.getFullYear()}-${today.getMonth() + 1}-${today.getDate() - 1}`);
  const [endDate, setEndDate] = useState<string>(`${today.getFullYear()}-${today.getMonth() + 1}-${today.getDate()}`);
  const [items, setItems] = useState([]);
  const [tableNames, setTableNames] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [totalAmount, setTotalAmount] = useState(0);
  const [totalPayout, setTotalPayout] = useState(0);
  const [serverRealAmount, setServerRealAmount] = useState(0);
  const [options, setOptions] = useState<Option[]>([]);

  const [selectedOption, setSelectedOption] = useState<Option | null>(null);

  const limit = 10; // Items per page

  useEffect(() => {
    let tempOptions: Option[] = [];
    tempOptions.push({ value: "", label: "All" });
    tableNames.map((item: any) => {
      tempOptions.push({ value: item, label: item });
    })
    setOptions(tempOptions);
  }, [tableNames])

  useEffect(() => {
    const fetchItems = async () => {
      const [startYear, startMonth, startDay] = startDate.split("-").map(Number); // Split and convert to numbers
      const [endYear, endMonth, endDay] = endDate.split("-").map(Number); // Split and convert to numbers
      const startLocalDate = new Date(startYear, startMonth - 1, startDay); // Create local date (month is 0-based)
      const endLocalDate = new Date(endYear, endMonth - 1, endDay); // Create local date (month is 0-based)
      startLocalDate.setHours(0, 0, 0, 0);
      endLocalDate.setHours(23, 59, 59, 999);
      if (playerContext?.playerId == undefined) return;
      try {
        const query = new URLSearchParams({
          start: startLocalDate.toISOString(),
          end: endLocalDate.toISOString(),
          created_by: playerContext?.playerId,
          filterName: selectedOption ? selectedOption.value : ""
        }).toString();
        const response = await fetch(`${API_ENDPOINT}/post/profit/${page}/${limit}?${query}`);
        const data = await response.json();
        setItems(data.profits);
        setTableNames(data.tableNames);
        setTotalPages(data.totalPages);
        if (page > data.totalPages) {
          setPage(1);
        }
        setTotalItems(data.totalItems);
        setTotalAmount(data.totalAmount);
        setTotalPayout(data.totalPayout);
        setServerRealAmount(data.serverRealAmount);
      } catch (error) {
        console.error("Error fetching items:", error);
      } finally {
      }
    };

    fetchItems();
  }, [page, startDate, endDate, selectedOption]);

  useEffect(() => {
    if (playerContext?.role == 3) {
      navigate('/');
    }
  }, [])

  const handlePrevPage = () => setPage(prev => Math.max(prev - 1, 1));
  const handleNextPage = () => setPage(prev => Math.min(prev + 1, totalPages));

  const convertToLocalTime = (date: string) => {
    const localDate = new Date(date);
    return localDate.toLocaleString();
  }

  const handleChange = (newValue: SingleValue<Option>) => {
    setSelectedOption(newValue);
  };

  return (
    <HomeWrapper>
      <Header />
      <StyleProfitContainer>
        <StyleTopBar>
          <h2>Profit Management</h2>
          <div className='date'>
            <div>
              <span>From:</span>
              <input
                type="date"
                defaultValue={`${today.getFullYear()}-${today.getMonth() + 1}-${today.getDate() - 1}`}
                onChange={(e) => setStartDate(e.target.value)}
                style={{height: '38px', borderRadius: '4px', border: 'none'}}
              />
            </div>
            <div>
              <span>To:</span>
              <input
                type="date"
                defaultValue={`${today.getFullYear()}-${today.getMonth() + 1}-${today.getDate()}`}
                onChange={(e) => setEndDate(e.target.value)}
                style={{height: '38px', borderRadius: '4px', border: 'none'}}
              />
            </div>
            <StyledTableNameFilter>
              <p>Table Name:</p>
              <Select
                value={selectedOption}
                onChange={handleChange}
                options={options}
              />
            </StyledTableNameFilter>
          </div>
        </StyleTopBar>
        <StyleTotalView>
          <div>
            <p>Total Betting Amount</p>
            <p>{totalAmount / 1000} SOL</p>
          </div>
          <div>
            <p>Total Payout Amount</p>
            <p>{totalPayout / 1000} SOL</p>
          </div>
          <div>
            <p>Profit</p>
            <p>{serverRealAmount / 1000} SOL</p>
          </div>
        </StyleTotalView>
        <p style={{ color: "#FFF", textAlign: 'end' }}>{totalItems} results</p>
        <StyleLeaderboardTable>
          <thead>
            <tr>
              <th>#</th>
              <th>Table Name</th>
              <th>Betting Amount</th>
              <th>Payout Amount</th>
              <th>Profit</th>
              <th>Date</th>
            </tr>
          </thead>
          <tbody>
            {
              items.map((item: any, index) => (
                <tr key={index}>
                  <td>{(page - 1) * 10 + index + 1}</td>
                  <td>{item.tableName}</td>
                  <td>{item.totalAmount / 1000} SOL</td>
                  <td>{item.totalPayout / 1000} SOL</td>
                  <td className={`${item.serverRealAmount > 0 ? "win" : item.serverRealAmount < 0 ? "lose" : "tie"}`}>{item.serverRealAmount / 1000} SOL</td>
                  <td>{convertToLocalTime(item.created_at)}</td>
                </tr>
              ))
            }
          </tbody>
        </StyleLeaderboardTable>
        <StylePagination>
          <button onClick={handlePrevPage} disabled={page === 1}>
            Previous
          </button>
          <span> Page {page} of {totalPages} </span>
          <button onClick={handleNextPage} disabled={page === totalPages}>
            Next
          </button>
        </StylePagination>
      </StyleProfitContainer>
      <Footer />
    </HomeWrapper>
  );
};